import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Snackbar, CircularProgress, Alert } from '@mui/material';
import { spacing } from 'common/theme';
import { deleteToken, setDefaultToken, getUserData } from 'redux/actions';
import { generateTestId } from 'app/functions';

import { PaymentCard } from './PaymentCard'; 
import { DeletePaymentModal } from './DeletePaymentModal';
import EmptyDataHot from 'components/EmptyDataHot';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { useTranslations } from 'common/hooks/useTranslations';
import Loader from 'components/common/loader';

export const PaymentMethodsList = ({ testID, paymentMethods = [], fullName }) => {
  const { translate } = useTranslations();
  const dispatch = useDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isSettingDefaultCardId, setIsSettingDefaultCardId] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const userData = useSelector((store) => store.config.UserData);

  const handleDeleteRequest = (card) => {
    setCardToDelete(card);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (cardToDelete) {
      dispatch(deleteToken({ tokenId: cardToDelete.id }));
      setIsDeleteModalOpen(false);
    }
  };

  const handleSetDefault = (cardId) => {
    setIsSettingDefault(true);
    setIsSettingDefaultCardId(cardId);
    dispatch(setDefaultToken({ tokenId: cardId }));
  };

  useEffect(() => {
    if (isSettingDefault && isSettingDefaultCardId && userData?.cardsArr) {
      const card = userData.cardsArr.find(c => c.id === isSettingDefaultCardId);
      if (card?.default) {
        setIsSettingDefault(false);
        setIsSettingDefaultCardId(null);
        setShowSuccessToast(true);
      }
    }
  }, [userData, isSettingDefault, isSettingDefaultCardId]);

  return (
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        flexWrap: 'wrap',
        gap: `${spacing.xl}px`,
      }}
    >
      {paymentMethods.length === 0 && (
        <EmptyDataHot
          testID={generateTestId(testID, 'empty-data')}
          title={translate('no_cards_placeholder')}
          image={NotInterestedIcon}
          imageSize={spacing.xxxxl}
        />
      )}
      {paymentMethods.length > 0 && paymentMethods.map((card) => (
        <Box key={card.id} sx={{ position: 'relative' }}>
          {isSettingDefault && isSettingDefaultCardId === card.id && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                borderRadius: `${spacing.md}px`,
              }}
            >
              <Loader/>
            </Box>
          )}
          <PaymentCard
            testID={generateTestId(testID, `card-${card.id}`)}
            cardNumber={card.last_digits}
            fullName={fullName}
            isDefault={card.default}
            onDelete={() => handleDeleteRequest(card)}
            onSelectDefault={() => handleSetDefault(card.id)}
          />
        </Box>
      ))}

      <DeletePaymentModal
        testID={generateTestId(testID, 'delete-modal')}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        lastDigits={cardToDelete?.last_digits}
      />

      <Snackbar
        open={showSuccessToast}
        autoHideDuration={2000}
        onClose={() => setShowSuccessToast(false)}
        direction="rtl"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessToast(false)} severity="success" sx={{ fontSize: '16px', fontWeight: 600 }}>
        {translate('credit_card_changed_success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};